import React, { useEffect, useState } from 'react';
import './ProjectSetupLoader.scss';
import { t } from 'i18next';
import { useAccount } from '../../../../../shared/contexts/account/AccountContexts';
import {
  SetupProgressSnapshot,
  getEnabledModelKeys,
} from '../../../../../shared/helpers/projectSetupProgress';
import { BOTS_ACTIVITY, GEO_CHECKLIST, GEO_REPORT, PROJECTS } from '../../../../../shared/routes/path';
import { PROJECT_SETUP_READY_MS, PROJECT_SETUP_MAX_MS } from '../analyze-loader/AnalyzeLoader';

type StepStatus = 'done' | 'active' | 'pending';

interface SetupStep {
  key: string;
  label: string;
  status: StepStatus;
}

// Rotating "in the meantime" cards — each links into a feature so the user has something useful to do
// while the data collects. `path` receives the new project id (GEO Audit is a global route, so it
// ignores it).
const VALUE_CARDS: {
  icon: string;
  titleKey: string;
  descKey: string;
  path: (projectId: number) => string;
}[] = [
  {
    icon: 'ico--report',
    titleKey: 'geoReport.navLabel',
    descKey: 'setupValueAuditDesc',
    path: () => GEO_REPORT,
  },
  {
    icon: 'ico--tick',
    titleKey: 'setupValueRecommendationsTitle',
    descKey: 'setupValueRecommendationsDesc',
    path: (id) => `${GEO_CHECKLIST}/${id}/topics-prompts`,
  },
  {
    icon: 'ico--bot',
    titleKey: 'botsActivity.navLabel',
    descKey: 'setupValueBotsDesc',
    path: (id) => `${BOTS_ACTIVITY}/${id}`,
  },
];

const VALUE_ROTATE_MS = 4500;

// The five setup steps are always the same (model-agnostic).
const STEP_COUNT = 5;
// Per-step durations. Steps 1-2 pace out over 3s each; step 3 (collecting) is 14s (→ 20s for steps
// 1-3); steps 4-5 are up to 15s each, but shrink to 2s once data has been collected so the tail wraps
// up quickly.
const STEP_INTERVAL_MS = 3_000;
const COLLECTING_MS = 14_000;
const TAIL_STEP_FULL_MS = 15_000;
const TAIL_STEP_SHORT_MS = 2_000;
// How often the timed reveal + flowing bar recompute.
const SETUP_TICK_MS = 250;

interface ProjectSetupLoaderProps {
  progress: SetupProgressSnapshot | null;
  projectId: number | null; // the new project's id, once POST /projects/add returned
  completed: boolean; // real work done — fill the bar to 100%
  onNavigate?: (path: string) => void; // leave the loader for a feature route (aborts the poll)
}

const ProjectSetupLoader: React.FC<ProjectSetupLoaderProps> = ({
  progress,
  projectId,
  completed,
  onNavigate,
}) => {
  const { accountResponse } = useAccount();
  const [card, setCard] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setCard((c) => (c + 1) % VALUE_CARDS.length), VALUE_ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  // Elapsed since the loader mounted — paces the timed step reveal and the flowing bar. Stops once the
  // real work is done (`completed`), so it can't keep ticking after we're about to navigate.
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (completed) return;
    const start = Date.now();
    const id = setInterval(() => setElapsedMs(Date.now() - start), SETUP_TICK_MS);
    return () => clearInterval(id);
  }, [completed]);

  const created = projectId != null;
  // Enabled models come from account features, so the step list is stable from the first frame — data
  // arriving only flips a row's state chip, never inserts/reorders rows (no ugly pop-in).
  const modelKeys = getEnabledModelKeys(accountResponse?.features ?? []);
  const readyByKey = new Map((progress?.models ?? []).map((m) => [m.key, m.hasData]));
  const allModelsReady = modelKeys.length > 0 && modelKeys.every((k) => readyByKey.get(k));

  // Cumulative completion times for steps 1-4 (the last step only turns "done" on `completed`). Steps
  // 4-5 shrink once data is ready, so the whole tail speeds up the moment collecting finishes.
  const tailStepMs = allModelsReady ? TAIL_STEP_SHORT_MS : TAIL_STEP_FULL_MS;
  const stepDoneAt = [
    STEP_INTERVAL_MS, // 1. brand added        → 10s
    2 * STEP_INTERVAL_MS, // 2. prompts selected → 20s
    2 * STEP_INTERVAL_MS + COLLECTING_MS, // 3. collecting → 40s
    2 * STEP_INTERVAL_MS + COLLECTING_MS + tailStepMs, // 4. visibility → 42s / 50s
  ];
  const timerDone = created ? stepDoneAt.filter((ms) => elapsedMs >= ms).length : 0;
  const doneCount = completed ? STEP_COUNT : Math.min(STEP_COUNT - 1, timerDone);

  const stepDefs: { key: string; label: string }[] = [
    { key: 'created', label: t('setupStepBrandAdded') },
    { key: 'prompts', label: t('setupStepPromptsSelected') },
    { key: 'collecting', label: t('setupStepCollectingResponses') },
    { key: 'visibility', label: t('setupStepVisibilityAnalysis') },
    { key: 'finalize', label: t('setupStepFinalization') },
  ];
  const steps: SetupStep[] = stepDefs.map((def, i) => ({
    ...def,
    status: i < doneCount ? 'done' : i === doneCount ? 'active' : 'pending',
  }));

  // Bar flows continuously toward the expected finish (44s once data is ready, else the 62s cap),
  // held below 100% until the real work completes.
  const expectedTotalMs = allModelsReady ? PROJECT_SETUP_READY_MS : PROJECT_SETUP_MAX_MS;
  const progressPct = completed
    ? 100
    : Math.min(95, Math.round((elapsedMs / expectedTotalMs) * 100));

  const stateLabel = (status: StepStatus): string =>
    status === 'done'
      ? t('setupStateDone')
      : status === 'active'
      ? t('setupStateInProgress')
      : t('setupStateWaiting');

  const value = VALUE_CARDS[card];

  return (
    <div className="project-setup-loader">
      <div className="project-setup-loader__belt">
        <div className="logo-box" />
      </div>

      <div className="project-setup-loader__card">
        <div className="project-setup-loader__main">
          <h2 className="project-setup-loader__title">{t('setupPreparingTitle')}</h2>
          <p className="project-setup-loader__subtitle">{t('setupPreparingSubtitle')}</p>

          <ul className="project-setup-loader__steps">
            {steps.map((step) => (
              <li
                key={step.key}
                className={`project-setup-loader__step project-setup-loader__step--${step.status}`}
              >
                <span className="project-setup-loader__step-marker" aria-hidden="true" />
                <span className="project-setup-loader__step-label">{step.label}</span>
                <span className="project-setup-loader__step-state">{stateLabel(step.status)}</span>
              </li>
            ))}
          </ul>

          <div className="project-setup-loader__bar">
            <span className="project-setup-loader__bar-fill" style={{ width: `${progressPct}%` }} />
          </div>

          <div className="project-setup-loader__note">
            <span className="ico--bell ico--sm project-setup-loader__note-icon" />
            <div>
              <p className="project-setup-loader__note-title">{t('setupNoteTitle')}</p>
              <p className="project-setup-loader__note-text">{t('setupNoteText')}</p>
            </div>
          </div>

          <div className="project-setup-loader__actions">
            {/* Revealed only once the project actually exists — jumps straight into its dashboard. */}
            {created && projectId != null && (
              <button
                type="button"
                className="project-setup-loader__open"
                onClick={() => onNavigate?.(`${PROJECTS}/${projectId}`)}
              >
                {t('openProject')}
              </button>
            )}
          </div>
        </div>

        <aside className="project-setup-loader__aside">
          <span className="project-setup-loader__aside-eyebrow">{t('setupMeanwhile')}</span>
          <p className="project-setup-loader__aside-lead">{t('setupMeanwhileLead')}</p>

          <div className="project-setup-loader__value" key={value.titleKey}>
            <span className={`project-setup-loader__value-icon ${value.icon} ico--lg`} />
            <p className="project-setup-loader__value-title">{t(value.titleKey)}</p>
            <p className="project-setup-loader__value-desc">{t(value.descKey)}</p>
            <button
              type="button"
              className="project-setup-loader__value-cta"
              disabled={!created}
              onClick={() => projectId != null && onNavigate?.(value.path(projectId))}
            >
              {t('setupOpen')}
            </button>
          </div>

          <div className="project-setup-loader__dots">
            {VALUE_CARDS.map((c, i) => (
              <button
                key={c.titleKey}
                type="button"
                className={`project-setup-loader__dot${
                  i === card ? ' project-setup-loader__dot--active' : ''
                }`}
                onClick={() => setCard(i)}
                aria-label={t(c.titleKey)}
              />
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ProjectSetupLoader;
